<script>
    import CRUD from './CRUD.svelte';
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import { 
        fetchCotizaDets, 
        saveCotizaDet, 
        removeCotizaDet, 
        searchCotizaDets, 
        subscribeToCotizaDets, 
        unsubscribeFromCotizaDets,
        updateCotizaCabTotal
    } from '../controllers/cotizaDetController.js';
    import { fetchProductos } from '../controllers/productoController.js';

    // Props para recibir el ID de la cotización
    export let cotizaCabId = null;

    // Configuración de las columnas para la tabla de detalles de cotización
    let cotizaDetColumns = [
        { label: 'Producto', field: 'producto', type: 'relation', relationField: 'nombre' },
        { label: 'Cantidad', field: 'cantidad', type: 'number' },
        { label: 'Precio Unitario', field: 'precio', type: 'currency' },
        { label: 'Total', field: 'total', type: 'currency' }
    ];

    // Estructura del nuevo objeto CotizaDet
    let newCotizaDet = {
        cotiza_cab: cotizaCabId,
        producto: null,
        cantidad: 1,
        precio: 0,
        total: 0
    };

    // Configuración de los campos para la edición de detalles de cotización
    let editColumns = [
        { 
            label: 'Producto', 
            key: 'producto', 
            type: 'select', 
            required: true, 
            fetchOptions: { key: 'id', value: 'nombre' }, 
            options: [] 
        },
        { label: 'Cantidad', key: 'cantidad', type: 'number', required: true, min: 1 },
        { label: 'Precio Unitario', key: 'precio', type: 'number', required: true, min: 0, step: 0.01 },
        { label: 'Total', key: 'total', type: 'number', required: true, readonly: true }
    ];

    // Opciones de productos
    let productoOptions = [];

    // Función para cargar y transformar productos
    async function loadProductos() {
        try {
            const fetchedProductos = await fetchProductos(1, 1000);

            if (Array.isArray(fetchedProductos.producto)) {
                productoOptions = fetchedProductos.producto.map(e => ({
                    key: e[editColumns.find(col => col.key === 'producto').fetchOptions.key],
                    value: e[editColumns.find(col => col.key === 'producto').fetchOptions.value],
                }));

                // Asigna las opciones transformadas a las columnas editables
                editColumns.find(column => column.key === 'producto').options = productoOptions;
            } else {
                console.error('Se esperaba un array de productos, pero se obtuvo:', fetchedProductos);
            }
        } catch (error) {
            console.error('Error al cargar los productos:', error);
        }
    }

    // Función para calcular el total cuando cambian cantidad o precio
    function calculateTotal(cantidad, precio) {
        return (cantidad || 0) * (precio || 0);
    }

    // Función personalizada para guardar que actualiza el total de la cotización
    async function saveCotizaDetWithTotal(cotizaDet, currentCotizaDetId) {
        try {
            // Calcular total del detalle
            cotizaDet.total = calculateTotal(cotizaDet.cantidad, cotizaDet.precio);
            
            // Guardar el detalle
            await saveCotizaDet(cotizaDet, currentCotizaDetId);
            
            // Actualizar total de la cotización
            if (cotizaCabId) {
                await updateCotizaCabTotal(cotizaCabId);
            }
        } catch (error) {
            console.error('Error saving cotiza det:', error);
        }
    }

    // Función personalizada para eliminar que actualiza el total de la cotización
    async function removeCotizaDetWithTotal(id) {
        try {
            // Eliminar el detalle
            await removeCotizaDet(id);
            
            // Actualizar total de la cotización
            if (cotizaCabId) {
                await updateCotizaCabTotal(cotizaCabId);
            }
        } catch (error) {
            console.error('Error removing cotiza det:', error);
        }
    }

    // Función para obtener detalles filtrados por cotización
    async function fetchCotizaDetsByCab(page, perPage) {
        if (!cotizaCabId) return { cotizaDet: [], totalRecords: 0, currentPage: 1 };
        return await fetchCotizaDets(cotizaCabId, page, perPage);
    }

    // Función para buscar detalles filtrados por cotización
    async function searchCotizaDetsByCab(query, page, perPage) {
        if (!cotizaCabId) return { cotizaDet: [], totalRecords: 0, currentPage: 1 };
        return await searchCotizaDets(cotizaCabId, query, page, perPage);
    }

    // Cargar los productos al montar el componente
    onMount(() => {
        loadProductos();
    });
</script>

<CRUD 
    title="Detalles de Cotización"
    entityName="cotiza_det"
    newEntity={newCotizaDet}
    columns={cotizaDetColumns}
    fetchEntities={fetchCotizaDetsByCab}
    saveEntity={saveCotizaDetWithTotal}
    removeEntity={removeCotizaDetWithTotal}
    searchEntities={searchCotizaDetsByCab}
    subscribeToChanges={subscribeToCotizaDets}
    unsubscribeFromChanges={unsubscribeFromCotizaDets}
    editColumns={editColumns}
    showNewButton={cotizaCabId !== null}
/>
